// DialogMetricasSistema — Popup com métricas em tempo real do servidor (CPU/RAM/Load/Uptime).
//
// Faz poll a cada 1.5s no endpoint /api/sistema/metricas e mantém um buffer
// circular dos últimos 80 pontos para renderizar gráficos de área animados (recharts).
//
// Cards de overview: % CPU, % RAM (com total e usado em GB), Load Average, Uptime.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Cpu, Memory, ChartLineUp, Timer } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const MAX_PONTOS = 80;       // 80 × 1.5s = 2 minutos de histórico
const INTERVALO_MS = 1500;

function formatarUptime(segundos) {
    if (!segundos || segundos < 0) return "—";
    const dias = Math.floor(segundos / 86400);
    const horas = Math.floor((segundos % 86400) / 3600);
    const min = Math.floor((segundos % 3600) / 60);
    if (dias > 0) return `${dias}d ${horas}h ${min}m`;
    if (horas > 0) return `${horas}h ${min}m`;
    return `${min}m`;
}

function formatarMB(mb) {
    if (!mb && mb !== 0) return "—";
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb} MB`;
}

function corPorPct(pct) {
    if (pct == null) return "text-zinc-500";
    if (pct < 60) return "text-emerald-400";
    if (pct < 85) return "text-yellow-400";
    return "text-red-400";
}

function hexPorPct(pct) {
    if (pct == null) return "#52525b";
    if (pct < 60) return "#34d399"; // emerald-400
    if (pct < 85) return "#facc15"; // yellow-400
    return "#f87171";               // red-400
}

export default function DialogMetricasSistema({ aberto, setAberto }) {
    const [historico, setHistorico] = useState([]);
    const [ultimo, setUltimo] = useState(null);
    const [online, setOnline] = useState(true);
    const ativo = useRef(true);

    useEffect(() => {
        if (!aberto) return undefined;
        ativo.current = true;
        const tick = async () => {
            try {
                const { data } = await api.get("/sistema/metricas");
                if (!ativo.current) return;
                setOnline(true);
                setUltimo(data);
                setHistorico((prev) => {
                    const t = new Date();
                    const rotulo = t.toLocaleTimeString("pt-BR", { hour12: false });
                    const novo = [...prev, {
                        t: rotulo,
                        cpu: Number((data.cpu_pct ?? 0).toFixed(1)),
                        ram: Number((data.ram_pct ?? 0).toFixed(1)),
                    }];
                    return novo.length > MAX_PONTOS ? novo.slice(-MAX_PONTOS) : novo;
                });
            } catch {
                if (ativo.current) setOnline(false);
            }
        };
        tick();
        const id = setInterval(tick, INTERVALO_MS);
        return () => { ativo.current = false; clearInterval(id); };
    }, [aberto]);

    // Reseta histórico ao fechar para começar limpo no próximo abrir
    useEffect(() => {
        if (!aberto) {
            setHistorico([]);
            setUltimo(null);
        }
    }, [aberto]);

    const corCPU = useMemo(() => hexPorPct(ultimo?.cpu_pct), [ultimo]);
    const corRAM = useMemo(() => hexPorPct(ultimo?.ram_pct), [ultimo]);

    return (
        <Dialog open={aberto} onOpenChange={setAberto}>
            <DialogContent
                className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl"
                data-testid="dialog-metricas-sistema"
            >
                <DialogTitle className="sr-only">Métricas do Servidor em tempo real</DialogTitle>

                {/* Cards de overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <CardMetrica
                        icone={<Cpu size={16} weight="duotone" className="text-emerald-400" />}
                        titulo="CPU"
                        valor={ultimo ? `${ultimo.cpu_pct.toFixed(1)}%` : "—"}
                        sub={ultimo ? `${ultimo.cpu_nucleos} núcleos` : ""}
                        cor={corPorPct(ultimo?.cpu_pct)}
                    />
                    <CardMetrica
                        icone={<Memory size={16} weight="duotone" className="text-blue-400" />}
                        titulo="RAM"
                        valor={ultimo ? `${ultimo.ram_pct.toFixed(1)}%` : "—"}
                        sub={ultimo ? `${formatarMB(ultimo.ram_usado_mb)} / ${formatarMB(ultimo.ram_total_mb)}` : ""}
                        cor={corPorPct(ultimo?.ram_pct)}
                    />
                    <CardMetrica
                        icone={<ChartLineUp size={16} weight="duotone" className="text-purple-400" />}
                        titulo="Load Avg"
                        valor={ultimo ? ultimo.load_avg_1m.toFixed(2) : "—"}
                        sub={ultimo ? `5m ${ultimo.load_avg_5m.toFixed(2)} · 15m ${ultimo.load_avg_15m.toFixed(2)}` : ""}
                        cor="text-purple-300"
                    />
                    <CardMetrica
                        icone={<Timer size={16} weight="duotone" className="text-amber-400" />}
                        titulo="Uptime"
                        valor={ultimo ? formatarUptime(ultimo.uptime_segundos) : "—"}
                        sub="desde o boot"
                        cor="text-amber-300"
                    />
                </div>

                {/* Gráfico CPU */}
                <SecaoGrafico
                    cor={corCPU}
                    rotulo="CPU"
                    chave="cpu"
                    dados={historico}
                    testId="grafico-cpu"
                />

                {/* Gráfico RAM */}
                <SecaoGrafico
                    cor={corRAM}
                    rotulo="RAM"
                    chave="ram"
                    dados={historico}
                    testId="grafico-ram"
                />
            </DialogContent>
        </Dialog>
    );
}

function CardMetrica({ icone, titulo, valor, sub, cor }) {
    return (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-md p-3 flex flex-col gap-1">
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <span className="flex items-center gap-1.5">{icone}{titulo}</span>
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${cor}`}>{valor}</div>
            <div className="text-[10px] text-zinc-500 truncate">{sub || "\u00a0"}</div>
        </div>
    );
}

function SecaoGrafico({ cor, rotulo, chave, dados, testId }) {
    return (
        <div className="bg-zinc-900/30 border border-zinc-800/80 rounded-md p-3" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2 flex items-center justify-between">
                <span>{rotulo} (%) — últimos {Math.min(dados.length, MAX_PONTOS)} pontos</span>
                <span className="tabular-nums text-zinc-400">
                    {dados.length > 0 ? `${dados[dados.length - 1][chave].toFixed(1)}%` : "—"}
                </span>
            </div>
            <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dados} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id={`grad-${chave}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={cor} stopOpacity={0.55} />
                                <stop offset="100%" stopColor={cor} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                        <XAxis
                            dataKey="t"
                            tick={{ fontSize: 9, fill: "#71717a" }}
                            stroke="#27272a"
                            interval="preserveStartEnd"
                            minTickGap={40}
                        />
                        <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 9, fill: "#71717a" }}
                            stroke="#27272a"
                            ticks={[0, 25, 50, 75, 100]}
                        />
                        <Tooltip
                            contentStyle={{
                                background: "#09090b",
                                border: "1px solid #3f3f46",
                                fontSize: 11,
                                color: "#fafafa",
                                borderRadius: 4,
                                padding: "4px 8px",
                            }}
                            labelStyle={{ color: "#a1a1aa", fontSize: 10 }}
                            formatter={(v) => [`${v}%`, rotulo]}
                        />
                        <Area
                            type="monotone"
                            dataKey={chave}
                            stroke={cor}
                            strokeWidth={1.6}
                            fill={`url(#grad-${chave})`}
                            isAnimationActive={false}
                            dot={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
